/**
 * Die Positionsauswertung: was wurde wie oft und in welcher Menge
 * abgerechnet, und was war es wert.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „ich will in der Auswertung wissen, einmal nachher die Positionen einzeln
 *    bzw. gesamt auswerten können über einen Zeitraum für die nächste
 *    Vertragsverhandlung, um zu sehen, wie viel Volumen wurde abgerufen an
 *    Menge und an Preis über welche Position. um da richtig verhandeln zu
 *    können ... was hat er für Positionen wie oft abgerechnet bei mir und zum
 *    Beispiel Baustellenbezogen, Tiefbau, dass ich sagen kann, er hat in der
 *    Region Limburg im letzten Monat so und so viele Baustellen mit Asphalt
 *    gehabt und so und so viele Baustellen mit Pflaster ... Dann eine
 *    Aufschlüsselung runter, um sogar bis auf jede einzelne Baustelle öffnen
 *    zu können."
 *
 * DREI EBENEN, UND JEDE IST EIN KLICK: Position → Region → Baustelle. Die
 * Zahl in der Zeile und die Liste dahinter kommen aus DERSELBEN Abfrage mit
 * DENSELBEN Filtern; sonst addiert sich das Aufgeklappte nicht zur Summe
 * darüber, und danach glaubt niemand mehr einer von beiden.
 *
 * GERECHNET WIRD AUF DEM SERVER. Hier steht keine einzige Summe: eine zweite
 * Rechnung im Browser liefe beim ersten Nachtrag anders aus als die auf dem
 * Bildschirm daneben.
 */

import { useEffect, useState } from 'react';
import { api, menge, baustelle } from '../api.ts';
import { Karte, Kachel, Leer } from './Geruest.tsx';

/** Euro, deutsch, mit zwei Nachkommastellen. */
function euro(w: unknown): string {
  if (w === null || w === undefined) return '-';
  const n = Number(w);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('de-DE',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Die vier Blöcke, für Menschen. */
const BLOCK_TEXT: Record<string, string> = {
  tiefbau: 'Tiefbau (Standardpositionen)',
  montage_fest: 'Montage, feste Positionen',
  montage_individuell: 'Montage, individuell',
  gebuehr: 'Durchgereichte Gebühren',
  innendienst: 'Nur Innendienst',
};

const BLOCK_KURZ: Record<string, string> = {
  tiefbau: 'Tiefbau',
  montage_fest: 'Montage fest',
  montage_individuell: 'Montage individuell',
  gebuehr: 'Gebühren',
  innendienst: 'Innendienst',
};

/** „12.10.2026 bis 14.10.2026", oder „am 14.10.2026". */
export function spanneText(von: string, bis: string): string {
  const d = (x: string) => x ? x.slice(0, 10).split('-').reverse().join('.') : '';
  if (!von || !bis) return 'im Zeitraum';
  return von === bis ? `am ${d(von)}` : `${d(von)} bis ${d(bis)}`;
}

export function Positionsauswertung({ von, bis }: { von: string; bis: string }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [grundlage, setGrundlage] = useState<'leb' | 'aufmass'>('leb');
  const [gemeinde, setGemeinde] = useState('');
  const [vertrag, setVertrag] = useState('');
  const [block, setBlock] = useState('');
  /* DREI FILTER DAZU (21.09.2026): „das dann alles filterbar, bzw onks,
     orte usw." Die Listen dahinter kommen aus den Daten und nicht aus einer
     festen Aufzählung - sonst steht irgendwann eine Firma zur Auswahl, die
     in diesem Zeitraum gar nicht gebaut hat. */
  const [sub, setSub] = useState('');
  const [onkz, setOnkz] = useState('');
  const [ort, setOrt] = useState('');
  /** Welche Position aufgeklappt ist, und was darunter steht. */
  const [offen, setOffen] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  const filter = {
    von, bis, grundlage,
    gemeinde: gemeinde || undefined,
    vertrag: vertrag || undefined,
    block: block || undefined,
    sub: sub || undefined,
    onkz: onkz || undefined,
    ort: ort || undefined,
  };

  useEffect(() => {
    if (!von || !bis) return;
    setD(null); setOffen(null); setDetail(null);
    api.auswertungPositionen(filter)
      .then(setD)
      .catch((f) => setFehler(String(f?.message ?? f)));
  }, [von, bis, grundlage, gemeinde, vertrag, block, sub, onkz, ort]);

  /** Eine Zeile aufklappen. Zu ist zu: ein zweiter Klick schliesst sie. */
  function klappe(oz: string) {
    if (offen === oz) { setOffen(null); setDetail(null); return; }
    setOffen(oz); setDetail(null);
    api.auswertungPositionDetail(oz, filter)
      .then(setDetail)
      .catch((f) => setFehler(String(f?.message ?? f)));
  }

  if (fehler) {
    return (
      <Karte titel="Positionen">
        <p className="hinweis warnung">{fehler}</p>
      </Karte>
    );
  }
  if (!d) return <Karte titel="Positionen"><Leer text="wird geladen …" /></Karte>;

  const summeBetrag = (d.bloecke ?? []).reduce(
    (s: number, b: any) => s + Number(b.betrag ?? 0), 0);
  const ohnePreis = (d.zeilen ?? []).reduce(
    (s: number, z: any) => s + Number(z.ohne_preis ?? 0), 0);

  /* DIE OBERFLÄCHEN JE REGION, in eine Tabelle gedreht: je Region eine Zeile,
     je Oberfläche eine Spalte. Marvins Frage lautet „wie viele Baustellen mit
     Asphalt UND wie viele mit Pflaster" - das ist ein Vergleich, und einen
     Vergleich liest man in einer Zeile, nicht in zwei Listen untereinander. */
  const oberflaechen: string[] = [...new Set(
    (d.oberflaechen ?? []).map((o: any) => o.oberflaeche as string))].sort() as string[];
  const regionen: string[] = [...new Set(
    (d.oberflaechen ?? []).map((o: any) => o.region as string))].sort() as string[];
  const feld = (region: string, art: string) =>
    (d.oberflaechen ?? []).find(
      (o: any) => o.region === region && o.oberflaeche === art);

  /* DASSELBE NOCH EINMAL, ABER AN DER ANDEREN ACHSE: je Firma eine Zeile,
     je Oberfläche eine Spalte. „wieviel mit pflaster, wieviel mit asphalt"
     beantwortet für eine Verhandlung nur dann etwas, wenn daneben steht,
     mit WEM. */
  const firmOberflaechen: string[] = [...new Set(
    (d.firmen_oberflaeche ?? []).map((o: any) => o.oberflaeche as string),
  )].sort() as string[];
  const firmFeld = (subId: string | null, art: string) =>
    (d.firmen_oberflaeche ?? []).find(
      (o: any) => o.subunternehmer_id === subId && o.oberflaeche === art);

  return (
    <>
      <Karte titel="Positionen"
             rechts={<span className="klein leise">
               {d.grundlage === 'leb' ? 'abgerechnet' : 'erfasst'}
             </span>}>
        {/* ZWEI GRUNDLAGEN, UND SIE WERDEN NIE ADDIERT.
            „Abgerechnet" steht in einer LEB-Datei, „erfasst" im Aufmaß;
            dieselbe Leistung steht in beiden. Welche gerade gilt, steht auf
            dem Knopf und über der Tabelle - eine Zahl, deren Grundlage man
            erfragen muss, ist keine Auskunft. */}
        <div className="reihe grundlagewahl">
          <button type="button"
                  className={'knopf' + (grundlage === 'leb' ? ' gewaehlt' : '')}
                  onClick={() => setGrundlage('leb')}
                  title="Was wirklich in einer LEB-Datei an die Telekom ging.">
            Abgerechnet (LEB)
          </button>
          <button type="button"
                  className={'knopf' + (grundlage === 'aufmass' ? ' gewaehlt' : '')}
                  onClick={() => setGrundlage('aufmass')}
                  title="Was im Aufmaß steht. Zwischen beiden liegen Wochen.">
            Erfasst (Aufmaß)
          </button>
          <select value={gemeinde} onChange={(e) => setGemeinde(e.target.value)}
                  aria-label="Region">
            <option value="">alle Regionen</option>
            {(d.gemeinden ?? []).map((g: any) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <select value={vertrag} onChange={(e) => setVertrag(e.target.value)}
                  aria-label="Rahmenvertrag">
            <option value="">alle Rahmenverträge</option>
            {(d.vertraege ?? []).map((v: any) => (
              <option key={v.id} value={v.id}>
                {v.nummer}{v.bezeichnung ? ` – ${v.bezeichnung}` : ''}
              </option>
            ))}
          </select>
          <select value={block} onChange={(e) => setBlock(e.target.value)}
                  aria-label="Block">
            <option value="">alle Blöcke</option>
            {Object.entries(BLOCK_TEXT).map(([k, t]) => (
              <option key={k} value={k}>{t}</option>
            ))}
          </select>
          {/* DIE FIRMA, DIE VORWAHL UND DER ORT.
              Die Auswahl zeigt, WIE VIELE Baustellen dahinterstehen. Ein
              Filter, der nach dem Klick eine leere Tabelle hinterlässt, ist
              eine Sackgasse; steht die Zahl daneben, klickt niemand hinein. */}
          <select value={sub} onChange={(e) => setSub(e.target.value)}
                  aria-label="Subunternehmer">
            <option value="">alle Firmen</option>
            {(d.firmen ?? [])
              .filter((f: any) => f.subunternehmer_id)
              .map((f: any) => (
                <option key={f.subunternehmer_id} value={f.subunternehmer_id}>
                  {f.firma} ({f.baustellen})
                </option>
              ))}
          </select>
          <select value={onkz} onChange={(e) => setOnkz(e.target.value)}
                  aria-label="Vorwahl">
            <option value="">alle Vorwahlen</option>
            {(d.vorwahlen ?? []).map((v: any) => (
              <option key={v.onkz} value={v.onkz}>
                {v.onkz}{v.bereich ? ` – ${v.bereich}` : ''} ({v.baustellen})
              </option>
            ))}
          </select>
          <select value={ort} onChange={(e) => setOrt(e.target.value)}
                  aria-label="Ort">
            <option value="">alle Orte</option>
            {(d.orte ?? []).map((o: any) => (
              <option key={o.ort} value={o.ort}>
                {o.ort} ({o.baustellen})
              </option>
            ))}
          </select>
        </div>

        <p className="klein leise">
          {d.grundlage === 'leb'
            ? 'ABGERECHNET: was in einer LEB-Datei an die Telekom gegangen ist. '
            : 'ERFASST: was im Aufmaß steht. Es ist noch nicht abgerechnet - '
              + 'dazwischen liegen Wochen. '}
          Der Preis kommt aus dem Rahmenvertrag des Auftrags, und zwar aus dem,
          der am Tag der Abrechnung galt. Die Gebührenposition 10099980 trägt
          den Eurobetrag als Menge und wird deshalb nie mit einem
          Einheitspreis multipliziert.
        </p>

        {/* WAS IN DER SUMME FEHLT, STEHT ÜBER DER SUMME.
            Eine Summe, in der die Hälfte der Zeilen mit null gerechnet hat,
            sieht aus wie eine Summe. */}
        {(d.auftraege_ohne_vertrag > 0 || ohnePreis > 0) && (
          <p className="hinweis warnung">
            {d.auftraege_ohne_vertrag > 0 && (
              <>{d.auftraege_ohne_vertrag} Auftrag/Aufträge in diesem Zeitraum
                tragen KEINEN Rahmenvertrag. </>
            )}
            {ohnePreis > 0 && (
              <>{ohnePreis} Zeile(n) haben keinen hinterlegten Preis. </>
            )}
            Diese Zeilen zählen bei der MENGE mit und fehlen beim BETRAG. Die
            Preise werden in den Stammdaten unter „Rahmenverträge" gepflegt.
          </p>
        )}

        <div className="kachelreihe">
          {(d.bloecke ?? []).map((b: any) => (
            <Kachel key={b.block}
                    wert={d.darfGeld ? euro(b.betrag) : `${b.auftraege}`}
                    was={d.darfGeld
                      ? `${BLOCK_KURZ[b.block] ?? b.block}, ${b.auftraege} Baustellen`
                      : `${BLOCK_KURZ[b.block] ?? b.block}, Baustellen`} />
          ))}
          {d.darfGeld && (
            <Kachel wert={euro(summeBetrag)} was="Volumen gesamt" />
          )}
        </div>

        {/* WO DIE FESTEN MONTAGEPOSITIONEN HERKOMMEN.

            ORIGINALTEXT MARVIN (08.09.2026)
              „Wo trage ich die festen Positionen für die Montage ein, die in
               die Gesamtauswertung gehen"

            Er hat vor DIESEM Bildschirm gestanden. Der Block „Montage, feste
            Positionen" entsteht aus den abgerechneten Zeilen; trägt keine
            Position den Haken, gibt es ihn NICHT - und ein Block, der nie
            erscheint, ist von einem abgeschafften nicht zu unterscheiden.

            ZWEI GRÜNDE, ZWEI NÄCHSTE SCHRITTE. Steht der Haken nirgends,
            gehört er gesetzt; steht er irgendwo und kam im Zeitraum keine
            solche Position vor, ist nichts zu tun. Ohne den Unterschied
            sucht jemand einen Pflegefehler, den es nicht gibt.

            UND DER SATZ STEHT NUR DA, WENN ER ETWAS SAGT. Kommt der Block
            vor, steht die Zahl daneben und beantwortet die Frage selbst -
            ein Hinweis, der immer dasteht, wird auch an dem Tag nicht
            gelesen, an dem er zutrifft. */}
        {!(d.bloecke ?? []).some((b: any) => b.block === 'montage_fest') && (
          <p className="klein leise">
            {d.feste_montagepositionen > 0 ? (
              <>Der Block „Montage, feste Positionen" fehlt in diesem
                Zeitraum: {d.feste_montagepositionen} Position(en) tragen den
                Haken, abgerechnet wurde davon hier keine.</>
            ) : (
              <>Es ist KEINE Position als feste Montageposition
                gekennzeichnet, deshalb gibt es den Block „Montage, feste
                Positionen" nicht. Der Haken heißt „Feste Montageposition" und
                steht in den Stammdaten unter „Leistungskatalog", an der
                einzelnen Position.</>
            )}
          </p>
        )}

        {/* WAS DIESE ZAHLEN NICHT SAGEN.
            Der Block „Tiefbau" sagt, welcher ANTEIL des Volumens auf
            Tiefbaupositionen entfällt - nicht, was der Tiefbau GEKOSTET hat.
            Was hinausgeht, ist die Rechnung des Nachunternehmers, und die
            trägt eine Summe und keine Position. Beides steht nebeneinander,
            und keins wird als das andere ausgegeben. */}
        {d.darfGeld && d.geld && (
          <>
            <div className="kachelreihe">
              {/* DER ZEITRAUM STEHT DABEI (.358). Marvin, 14.10.2026: „die
                  Rechnungen der Nachunternehmer, der Wert ist genullt". Er
                  war richtig - eingestellt war „diese Woche", und darin lag
                  noch keine Rechnung. Das stand nirgends an der Zahl. */}
              <Kachel wert={euro(d.geld.gutschriften)}
                      was={`Gutschriften der Telekom, ${spanneText(von, bis)}`} />
              <Kachel wert={euro(d.geld.sub_rechnungen)}
                      was={`Rechnungen der Nachunternehmer, ${spanneText(von, bis)}`} />
            </div>
            <p className="klein leise">
              Die Blöcke oben sagen, WORAUS SICH DAS VOLUMEN ZUSAMMENSETZT.
              Sie sagen nicht, was der Tiefbau oder die Montage gekostet hat:
              was hinausgeht, ist die Rechnung des Nachunternehmers, und die
              trägt in diesem System eine Summe und keine Position. Die beiden
              Zahlen daneben sind die einzigen, bei denen wirklich Geld
              geflossen ist.
            </p>
          </>
        )}

        {!d.zeilen?.length ? (
          <Leer text={'In diesem Zeitraum ist nichts '
            + (d.grundlage === 'leb' ? 'abgerechnet' : 'erfasst') + ' worden.'} />
        ) : (
          <div className="rollt">
            <table className="liste positionsliste">
              <thead>
                <tr>
                  <th>OZ</th>
                  <th>Position</th>
                  <th>Block</th>
                  <th className="zahl" title="Wie viele BAUSTELLEN, nicht wie viele Zeilen.">
                    Baustellen
                  </th>
                  <th className="zahl">Zeilen</th>
                  <th className="zahl">Menge</th>
                  {d.darfGeld && <th className="zahl">Preis</th>}
                  {d.darfGeld && <th className="zahl">Betrag</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.zeilen.map((z: any) => (
                  <PositionZeile key={z.oz} z={z} offen={offen === z.oz}
                                 detail={offen === z.oz ? detail : null}
                                 darfGeld={d.darfGeld}
                                 klick={() => klappe(z.oz)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>

      {/* DIE OBERFLÄCHEN JE REGION - Marvins Beispiel wörtlich.
          GEZÄHLT WERDEN BAUSTELLEN und nicht Zeilen: ein Graben je Abschnitt
          ergibt fünf Zeilen an einem Haus. */}
      {oberflaechen.length > 0 && (
        <Karte titel="Baustellen je Oberfläche und Region">
          <p className="klein leise">
            Gezählt werden BAUSTELLEN, nicht Positionszeilen. Ein Graben je
            Abschnitt ergibt mehrere Zeilen an einem Haus.
          </p>
          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th>Region</th>
                  {oberflaechen.map((o) => <th key={o} className="zahl">{o}</th>)}
                </tr>
              </thead>
              <tbody>
                {regionen.map((r) => (
                  <tr key={r}>
                    <td>{r}</td>
                    {oberflaechen.map((o) => {
                      const f = feld(r, o);
                      return (
                        <td key={o} className="zahl"
                            title={f ? `${menge(Number(f.menge))} insgesamt` : ''}>
                          {f ? f.baustellen : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karte>
      )}

      {/* WELCHER TIEFBAUER WELCHE BAUSTELLEN HATTE.
          ORIGINALTEXT MARVIN (21.09.2026): „aber auch welcher Tiefbauer
          welche baustellen hatte, wieviel mit pflaster, wieviel mit asphalt".

          DIE ZEILE OHNE FIRMA BLEIBT STEHEN. Volumen, das keiner Firma
          zugeordnet ist, ist eine Auskunft und kein Nichts - meistens heisst
          es, dass am Gewerk kein Subunternehmer eingetragen ist. */}
      {(d.firmen ?? []).length > 0 && (
        <Karte titel="Baustellen je Firma">
          <p className="klein leise">
            Die Firma kommt vom GEWERK: Tiefbaupositionen vom Gewerk TB,
            Montagepositionen von EB oder SP. Stornierte Gewerke zählen
            nicht mit. Gezählt werden BAUSTELLEN, nicht Positionszeilen.
          </p>
          <div className="rollt">
            <table className="liste">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th className="zahl">Baustellen</th>
                  <th className="zahl">Zeilen</th>
                  {firmOberflaechen.map((o) => (
                    <th key={o} className="zahl">{o}</th>
                  ))}
                  {d.darfGeld && <th className="zahl">Betrag</th>}
                </tr>
              </thead>
              <tbody>
                {(d.firmen ?? []).map((f: any) => (
                  <tr key={f.subunternehmer_id ?? 'ohne'}>
                    <td>
                      {f.firma
                        ? <>{f.firma}{f.kurzzeichen
                             ? <span className="leise"> ({f.kurzzeichen})</span>
                             : null}</>
                        : <span className="leise">ohne Firma am Gewerk</span>}
                    </td>
                    <td className="zahl">{f.baustellen}</td>
                    <td className="zahl">{f.zeilen}</td>
                    {firmOberflaechen.map((o) => {
                      const w = firmFeld(f.subunternehmer_id, o);
                      return (
                        <td key={o} className="zahl"
                            title={w ? `${menge(Number(w.menge))} insgesamt` : ''}>
                          {w ? w.baustellen : '-'}
                        </td>
                      );
                    })}
                    {d.darfGeld && <td className="zahl">{euro(f.betrag)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Karte>
      )}
    </>
  );
}

/**
 * Eine Positionszeile mit ihrer Aufschlüsselung darunter.
 *
 * SIE STEHT IN DERSELBEN TABELLE und nicht in einer zweiten daneben. Eine
 * eigene Tabelle je aufgeklappter Zeile rechnet ihre Spaltenbreiten aus IHREM
 * Inhalt, und dann stehen Menge und Betrag auf einer anderen Höhe als in der
 * Zeile darüber - dieselbe Lehre wie beim Aufmaß am 01.09.2026.
 */
function PositionZeile({
  z, offen, detail, darfGeld, klick,
}: {
  z: any; offen: boolean; detail: any; darfGeld: boolean; klick: () => void;
}) {
  const spalten = darfGeld ? 9 : 7;
  const preis = z.preis_min === null || z.preis_min === undefined
    ? '-'
    : Number(z.preis_min) === Number(z.preis_max)
      ? euro(z.preis_min)
      /* ZWEI PREISE IN EINER ZEILE SIND EINE AUSKUNFT UND KEIN FEHLER: der
         Zeitraum reicht über eine Preisanpassung oder über zwei
         Rahmenverträge. Wer daraus einen Mittelwert macht, verschweigt genau
         das, wonach man in einer Verhandlung sucht. */
      : `${euro(z.preis_min)} – ${euro(z.preis_max)}`;

  return (
    <>
      <tr className={offen ? 'markiert' : undefined}>
        <td><code>{z.oz}</code></td>
        <td>{z.bezeichnung ?? <span className="leise">nicht im Katalog</span>}</td>
        <td className="klein leise">{BLOCK_KURZ[z.block] ?? z.block}</td>
        <td className="zahl einfarbig">{z.auftraege}</td>
        <td className="zahl einfarbig">{z.zeilen}</td>
        <td className="zahl einfarbig">{menge(Number(z.menge), z.einheit ?? '')}</td>
        {darfGeld && <td className="zahl einfarbig">{preis}</td>}
        {darfGeld && (
          <td className="zahl einfarbig"
              title={z.ohne_preis > 0
                ? `${z.ohne_preis} Zeile(n) ohne Preis fehlen in diesem Betrag.`
                : ''}>
            {euro(z.betrag)}
            {z.ohne_preis > 0 && <span className="warnfarbe"> *</span>}
          </td>
        )}
        <td className="knopfleiste">
          <button type="button" className="knopf klein" onClick={klick}
                  title="Die Regionen und jede einzelne Baustelle dahinter">
            {offen ? 'zuklappen' : 'aufklappen'}
          </button>
        </td>
      </tr>
      {offen && (
        <tr className="aufgeklappt">
          <td colSpan={spalten}>
            {!detail ? (
              <Leer text="wird geladen …" />
            ) : (
              <div className="aufschluesselung">
                <h4>Regionen</h4>
                <div className="rollt">
                  <table className="liste">
                    <thead>
                      <tr>
                        <th>Region</th>
                        <th className="zahl">Baustellen</th>
                        <th className="zahl">Menge</th>
                        {darfGeld && <th className="zahl">Betrag</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.regionen.map((r: any) => (
                        <tr key={r.region ?? '-'}>
                          <td>{r.region ?? <span className="leise">ohne Gemeinde</span>}</td>
                          <td className="zahl einfarbig">{r.baustellen}</td>
                          <td className="zahl einfarbig">
                            {menge(Number(r.menge), z.einheit ?? '')}
                          </td>
                          {darfGeld && (
                            <td className="zahl einfarbig">{euro(r.betrag)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h4>Jede einzelne Baustelle</h4>
                {detail.gekappt && (
                  <p className="hinweis warnung">
                    Es werden die 500 grössten gezeigt. Wer alle braucht,
                    engt den Zeitraum oder die Region ein.
                  </p>
                )}
                <div className="rollt">
                  <table className="liste">
                    <thead>
                      <tr>
                        <th>Baustelle</th>
                        <th>Region</th>
                        <th>Rahmenvertrag</th>
                        <th className="zahl">Menge</th>
                        {darfGeld && <th className="zahl">Betrag</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.baustellen.map((b: any) => (
                        <tr key={b.auftrag_id}>
                          {/* DIE KOMPLETTE ANSCHRIFT, mit PLZ, KLS und SM.
                              Sie steht seit .91 an EINER Stelle - der Satz
                              stand vorher achtmal von Hand im Haus, und in
                              allen acht fehlte die Postleitzahl. */}
                          <td>{baustelle(b)}</td>
                          <td>{b.region ?? ''}</td>
                          <td className="klein leise">
                            {b.rahmenvertrag ?? 'keiner'}
                          </td>
                          <td className="zahl einfarbig">
                            {menge(Number(b.menge), z.einheit ?? '')}
                          </td>
                          {darfGeld && (
                            <td className="zahl einfarbig">{euro(b.betrag)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
